/**
 * Asset Data Quality — issue detection.
 *
 * Pure functions. Take a row's monthly CO₂e values and a set of detection
 * thresholds; return per-month issue lists. The grid then merges these
 * with gap detection (no-activity months) to colour each cell.
 *
 * Severity hierarchy: critical > warning > clean.
 *
 * The detection rules deliberately stay simple — anomaly detection on
 * 12 monthly buckets per source isn't a place for k-means or LSTMs. We
 * give the user enough levers (variance %, z-score σ, PoP jump %, and a
 * critical/warning split) to tune false-positive rates without letting
 * the model overfit a year-long series.
 */

export type IssueSeverity = 'warning' | 'critical';

export type IssueRule = 'variance' | 'zscore' | 'pop' | 'gap';

export interface CellIssue {
  rule: IssueRule;
  severity: IssueSeverity;
  message: string;
}

/** A single (row × month) cell. `value === null` ⇒ no activity recorded for that bucket. */
export interface CellInput {
  value: number | null;
}

export interface DetectionThresholds {
  /** Master enable for variance-from-rolling-average rule. */
  varianceEnabled: boolean;
  /** Variance % above which a month is flagged as `warning`. e.g. 25 ⇒ ±25%. */
  variancePct: number;
  /** Variance % above which a month is flagged as `critical` (warning is the lower bound). */
  varianceCriticalPct: number;
  /** Master enable for z-score rule. */
  zScoreEnabled: boolean;
  /** Z-score σ above which a month is flagged as `warning`. */
  zScoreSigma: number;
  /** Z-score σ above which a month is flagged as `critical`. */
  zScoreCriticalSigma: number;
  /** Master enable for period-over-period rule. */
  popEnabled: boolean;
  /** % MoM jump that flags a month as `warning`. */
  popPct: number;
  /** % MoM jump that flags a month as `critical`. */
  popCriticalPct: number;
}

export const DEFAULT_THRESHOLDS: DetectionThresholds = {
  varianceEnabled: true,
  variancePct: 25,
  varianceCriticalPct: 50,
  zScoreEnabled: true,
  zScoreSigma: 2,
  zScoreCriticalSigma: 3,
  popEnabled: true,
  popPct: 50,
  popCriticalPct: 100,
};

const ROLLING_WINDOW = 3;

/**
 * Rolling average of the previous N values (excluding the current index).
 * Returns null when fewer than 2 prior values exist — without enough history,
 * "variance from average" is meaningless and would fire on every cell.
 */
const rollingAverage = (values: ReadonlyArray<number | null>, idx: number): number | null => {
  const window: number[] = [];
  for (let i = Math.max(0, idx - ROLLING_WINDOW); i < idx; i++) {
    const v = values[i];
    if (typeof v === 'number') window.push(v);
  }
  if (window.length < 2) return null;
  return window.reduce((sum, v) => sum + v, 0) / window.length;
};

/**
 * Mean and population standard deviation across all non-null values in the
 * row. Excludes the current cell so a single outlier doesn't suppress its
 * own z-score. Returns null mean when there's < 3 data points.
 */
const rowStats = (values: ReadonlyArray<number | null>, excludeIdx: number): { mean: number; sigma: number } | null => {
  const sample: number[] = [];
  for (let i = 0; i < values.length; i++) {
    if (i === excludeIdx) continue;
    const v = values[i];
    if (typeof v === 'number') sample.push(v);
  }
  if (sample.length < 3) return null;
  const mean = sample.reduce((sum, v) => sum + v, 0) / sample.length;
  const variance = sample.reduce((sum, v) => sum + (v - mean) ** 2, 0) / sample.length;
  return { mean, sigma: Math.sqrt(variance) };
};

const formatPct = (pct: number): string => `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`;

/**
 * Detect issues for a single row across all months. Returns an array
 * parallel to the input — `issues[i]` lists every rule that fired at month `i`.
 */
export const detectRowIssues = (
  values: ReadonlyArray<CellInput>,
  thresholds: DetectionThresholds,
): CellIssue[][] => {
  const numericValues = values.map((c) => c.value);
  const stats = rowStats(numericValues, -1);
  return values.map((cell, idx) => {
    const issues: CellIssue[] = [];
    if (cell.value === null) return issues; // gap — handled separately

    // ── Variance vs rolling average ─────────────────────────────────────
    if (thresholds.varianceEnabled) {
      const avg = rollingAverage(numericValues, idx);
      if (avg !== null && avg > 0) {
        const deltaPct = ((cell.value - avg) / avg) * 100;
        const abs = Math.abs(deltaPct);
        if (abs >= thresholds.varianceCriticalPct) {
          issues.push({
            rule: 'variance',
            severity: 'critical',
            message: `${formatPct(deltaPct)} vs prior 3-month average`,
          });
        } else if (abs >= thresholds.variancePct) {
          issues.push({
            rule: 'variance',
            severity: 'warning',
            message: `${formatPct(deltaPct)} vs prior 3-month average`,
          });
        }
      }
    }

    // ── Z-score across the row ──────────────────────────────────────────
    if (thresholds.zScoreEnabled && stats && stats.sigma > 0) {
      const z = (cell.value - stats.mean) / stats.sigma;
      const absZ = Math.abs(z);
      if (absZ >= thresholds.zScoreCriticalSigma) {
        issues.push({
          rule: 'zscore',
          severity: 'critical',
          message: `${z >= 0 ? '+' : ''}${z.toFixed(1)}σ from row mean`,
        });
      } else if (absZ >= thresholds.zScoreSigma) {
        issues.push({
          rule: 'zscore',
          severity: 'warning',
          message: `${z >= 0 ? '+' : ''}${z.toFixed(1)}σ from row mean`,
        });
      }
    }

    // ── Period-over-period jump ─────────────────────────────────────────
    if (thresholds.popEnabled && idx > 0) {
      const prev = numericValues[idx - 1];
      if (typeof prev === 'number' && prev > 0) {
        const deltaPct = ((cell.value - prev) / prev) * 100;
        const abs = Math.abs(deltaPct);
        if (abs >= thresholds.popCriticalPct) {
          issues.push({
            rule: 'pop',
            severity: 'critical',
            message: `${formatPct(deltaPct)} vs previous month`,
          });
        } else if (abs >= thresholds.popPct) {
          issues.push({
            rule: 'pop',
            severity: 'warning',
            message: `${formatPct(deltaPct)} vs previous month`,
          });
        }
      }
    }

    return issues;
  });
};

/** Most-severe severity across an issue list, or undefined when empty. */
export const peakSeverity = (issues: ReadonlyArray<CellIssue>): IssueSeverity | undefined => {
  let peak: IssueSeverity | undefined;
  for (const i of issues) {
    if (i.severity === 'critical') return 'critical';
    if (i.severity === 'warning') peak = 'warning';
  }
  return peak;
};
